import { ChangeEvent, FC, useEffect, useState } from 'react';
import { FormControl } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Card, CardContent } from './CampaignWizardStyles';

import { CampaignWizardStepProps } from './CampaignWizard';
import { CDTextField } from '../../../../components/CDTextField';
import Column from '../../../../components/Column';
import OemProgramAutocomplete from '../../../../components/Form/Fields/OemProgramAutocomplete';
import { Campaign } from '../../../../types/Campaign';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { OemProgram } from '../../../../types/DspEntity';
import { useCampaignWizardContext } from '../../../../hooks/useCampaignWizard';
import { useUserContext } from '../../../../hooks/Contexts/UserContext';

interface CampaignOemProgramFieldProps {
    campaign: Campaign;
    onChange(value: Partial<Campaign>): void;
    onEmpty?: () => void;
    onNonEmpty?: () => void;
}

const CampaignOemProgramField: FC<CampaignOemProgramFieldProps> = ({ campaign, onChange, onEmpty, onNonEmpty }) => {
    const { userContext } = useUserContext();
    const $campaignWizard = useCampaignWizardContext();
    const { isDraftCampaign, isNewCampaign } = $campaignWizard;

    const { data, isFetched } = useQuery({
        queryKey: ['campaign.options.oemPrograms', campaign.dealerId],
        queryFn: async () => {
            const { data } = await axios.get('/api/oem-program/filter', {
                headers: { XBusy: 'false' },
                params: {
                    advertiserIds: campaign.dealerId,
                },
            });
            return data.content as OemProgram[];
        },
    });

    // Trigger callback when data is empty (after fetch completes)
    useEffect(() => {
        if (!isFetched) return;

        if (!data || data.length === 0) {
            onEmpty?.();
        } else {
            onNonEmpty?.();
        }
    }, [isFetched, data, onEmpty, onNonEmpty]);

    return (
        <OemProgramAutocomplete
            label="OEM Program"
            options={data}
            value={campaign.oemProgramId ?? null}
            disabled={!isNewCampaign && !isDraftCampaign && !userContext.isAdmin()}
            onChange={(value: any) => {
                onChange({
                    oemProgramId: value ? value.id : undefined,
                    oemProgram: value ?? undefined,
                });
            }}
        />
    );
};

type CampaignInfoCardCardProps = {} & CampaignWizardStepProps;

export default function CampaignInfoCardCard(props: CampaignInfoCardCardProps) {
    const { campaign, onChange } = props;
    const { userContext } = useUserContext();

    const [hasOemPrograms, setHasOemPrograms] = useState<boolean>(true);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.name) {
            switch (event.target.name) {
                case 'gatewayAmazonDSPIsActive':
                    onChange({
                        [event.target.name]: event.target.checked ? 2 : 0,
                    });
                    break;

                default:
                    onChange({
                        [event.target.name]: event.target.value,
                    });
                    break;
            }
        }
    };

    return (
        <Card id="campaign-wizard-review-campaign-name" variant="outlined">
            <CardContent>
                <Column>
                    <Grid container spacing={3}>
                        <Grid size={8}>
                            <FormControl fullWidth size="small">
                                <CDTextField
                                    variant="outlined"
                                    label="Campaign Name"
                                    name="campaignName"
                                    required={true}
                                    value={campaign.campaignName}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </Grid>

                        {hasOemPrograms && !userContext.isOpsAdmin() && (
                            <Grid size={8}>
                                <FormControl fullWidth size="small">
                                    <CampaignOemProgramField
                                        campaign={campaign}
                                        onChange={onChange}
                                        onEmpty={() => setHasOemPrograms(false)}
                                        onNonEmpty={() => setHasOemPrograms(true)}
                                    />
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>
                </Column>
            </CardContent>
        </Card>
    );
}
